import React, { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Container,
  Header,
  KeyValuePairs,
  LiveRegion,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { OmsProduct, Stage } from "../../lusca-search-api/generated-src";
import { useOmsOwnedExport } from "../../hooks/useOmsOwnedExport";
import { useOmsIdentity } from "../../hooks/omsContextIdentity";
import { OmsReportsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { reportTimestamp } from "./OMSReportSchedule";
import { fileSize } from "./omsReportFormatting";
import OMSWorkflow from "./OMSWorkflow";

export default function OMSOwnedExportResult({
  product,
  jobId,
}: {
  product: OmsProduct;
  jobId: string;
}) {
  const { financialAccount } = useOmsIdentity(product);
  const headingRoot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const title = headingRoot.current?.querySelector("h2");
    title?.setAttribute("tabindex", "-1");
    title?.focus();
  }, [jobId, product, financialAccount]);
  const query = useOmsOwnedExport(product, jobId);
  const download = useMutation({
    mutationFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExportDownload(
          product,
          Stage.Prod,
          jobId,
          financialAccount
        )
      ).data,
    onSuccess: (result) => window.location.assign(result.url),
  });
  const job = query.data;
  const footer = (
    <div className="oms-report-download__footer-actions oms-report-download__footer-actions--export">
      {job?.status === "READY" && !query.isError && (
        <Button
          variant="primary"
          iconName="download"
          loading={download.isLoading}
          onClick={() => download.mutate()}
        >
          Download ZIP
        </Button>
      )}
    </div>
  );
  if (query.isError)
    return (
      <OMSWorkflow
        heading="Export result"
        stage="result"
        footer={footer}
        focusHeading
      >
        <Alert
          type="warning"
          header="Export unavailable"
          action={
            <Button
              onClick={() => {
                query.refetch().catch(() => undefined);
              }}
            >
              Retry status
            </Button>
          }
        >
          This export may be expired or unavailable for this account and user.
        </Alert>
      </OMSWorkflow>
    );
  return (
    <div ref={headingRoot}>
      <OMSWorkflow heading="Export result" stage="result" footer={footer}>
        <Container
          className="oms-workflow-section"
          header={<Header variant="h3">Export status</Header>}
        >
          <SpaceBetween size="m">
            <LiveRegion>
              <StatusIndicator
                type={
                  !job || job.status === "PREPARING"
                    ? "in-progress"
                    : job.status === "READY"
                    ? "success"
                    : "error"
                }
              >
                {job?.status === "READY"
                  ? "Export ready"
                  : job?.status === "FAILED"
                  ? "Export failed"
                  : "Preparing export"}
              </StatusIndicator>
            </LiveRegion>
            {job && (
              <KeyValuePairs
                columns={3}
                items={[
                  {
                    label: "Original report refreshed",
                    value: reportTimestamp(job.sourceRefreshedAt),
                  },
                  {
                    label: "Matching rows",
                    value:
                      job.rowCount?.toLocaleString() ||
                      "Available after preparation",
                  },
                  {
                    label: "Download available until",
                    value: reportTimestamp(job.expiresAt),
                  },
                ]}
              />
            )}
            {job?.status === "PREPARING" && (
              <p>You can return to the dashboard while this export prepares.</p>
            )}
            {job?.status === "FAILED" && (
              <Alert type="error">
                {job.error ||
                  "Return to the dashboard to start another export."}
              </Alert>
            )}
            {job?.status === "READY" && (
              <p>
                One CSV with column headers, compressed in a ZIP
                {job.sizeBytes ? ` (${fileSize(job.sizeBytes)})` : ""}.
              </p>
            )}
            {download.isError && (
              <Alert type="error">
                The download is unavailable. Retry while the export has not
                expired.
              </Alert>
            )}
            <LiveRegion>
              {download.isSuccess &&
                "Sent to browser downloads. View transfer progress in your browser."}
            </LiveRegion>
          </SpaceBetween>
        </Container>
      </OMSWorkflow>
    </div>
  );
}
